import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  List,
  ListItem,
  Typography
} from '@mui/material';
import { User, VoterGroup } from '../../types';

import React from 'react';

interface VoterGroupCardProps {
  group: VoterGroup;
  user: User;
  isLastGroup?: boolean;
  lastGroupRef?: React.Ref<HTMLDivElement>;
  onSelect?: (group: VoterGroup) => void;
  onEdit?: (group: VoterGroup) => void;
  onDelete?: (group: VoterGroup) => void;
}

const VoterGroupCard: React.FC<VoterGroupCardProps> = ({ 
  group, 
  user,
  isLastGroup = false,
  lastGroupRef,
  onSelect, 
  onEdit, 
  onDelete
}) => {
  // Construct location string
  const locationParts = [
    group.purok && `Purok ${group.purok}`,
    group.barangay,
    group.city_municipality,
    group.province
  ].filter(Boolean);

  // Determine if sub-groups should be shown
  const showSubGroups = !['independent', 'family'].includes(group.level);

  // Render numbered list with proper alignment
  const renderNumberedList = (items: any[], title?: string) => {
    if (!items || items.length === 0) return null;

    return (
      <>
        {title && (
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {title}
          </Typography>
        )}
        <List dense sx={{ py: 0 }}>
          {items.map((item, index) => (
            <ListItem 
              key={item.id} 
              sx={{ 
                py: 0.5, 
                display: 'flex', 
                alignItems: 'start' 
              }}
            >
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mr: 2, 
                  width: '20px', 
                  textAlign: 'right' 
                }}
              >
                {index + 1}.
              </Typography>
              <Typography variant="body2">
                {item.name}
              </Typography>
            </ListItem>
          ))}
        </List>
      </>
    );
  };

  return (
    <Card 
      key={group.id} 
      sx={{ mb: 2 }}
      ref={isLastGroup ? lastGroupRef : null}
    >
      <CardContent>
        {/* Group Name */}
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
          {group.name}
        </Typography>

        {/* Location */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {locationParts.join(', ')}
        </Typography>

        {/* Level */}
        <Typography variant="body2" sx={{ mb: 2 }}>
          Level: {group.level}
        </Typography>

        {/* Leader */}
        <Typography variant="body2" sx={{ mb: 1 }}>
          Leader: {group.leader.name}
        </Typography>

        {/* Members Section */}
        <Typography variant="body2" sx={{ mb: 1 }}>
          Members: {group.members?.length || 0}
          {showSubGroups ? ` | Sub-groups: ${group.subGroups?.length || 0}` : ''}
        </Typography>

        {/* Members List */}
        {group.members && group.members.length > 0 && (
          renderNumberedList(group.members)
        )}

        {/* Sub-groups Section */}
        {showSubGroups && group.subGroups && group.subGroups.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            {renderNumberedList(group.subGroups, 'Sub-groups')}
          </>
        )}

        {/* Empty state */}
        {(!group.members || group.members.length === 0) && 
         (!showSubGroups || !group.subGroups || group.subGroups.length === 0) && (
          <Typography variant="body2" color="text.secondary">
            No members or sub-groups in this group
          </Typography>
        )}
      </CardContent>
      
      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
        {onSelect && (
          <Button 
            size="small" 
            color="primary" 
            onClick={() => onSelect(group)}
            sx={{ mr: 1 }}
          >
            Select
          </Button>
        )}
        {user.permissions?.manage_voter_groups && onEdit && (
          <Button 
            size="small" 
            color="primary" 
            onClick={() => onEdit(group)} 
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
        )}
        {(user.permissions?.admin || user.permissions?.delete_voter_groups) && onDelete && (
          <Button 
            size="small" 
            color="error" 
            onClick={() => onDelete(group)}
          >
            Delete
          </Button>
        )}
      </Box>
    </Card>
  );
};

export default VoterGroupCard;
